import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { getCachedCategories } from '../cache/appDataCache';

interface PushedDataRecord {
  id: string;
  websiteUrl: string;
  publisherName?: string;
  publisherEmail?: string;
  da?: number;
  dr?: number;
  traffic?: number;
  ss?: number;
  keywords?: number;
  category?: string;
  country?: string;
  language?: string;
  tat?: string;
  gbBasePrice?: number;
  liBasePrice?: number;
  status: string;
  negotiationStatus: string;
  mainProjectId: string;
  pushedAt: string;
  pushedBy?: string;
  imageLink?: string;
  pushedByUser?: {
    firstName: string;
    lastName: string;
  };
}

// Fallback categories if API fails
const FALLBACK_CATEGORIES = [
  { value: 'AI_TOOL', label: 'AI Tool' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'CASINO', label: 'Casino' },
  { value: 'CBD', label: 'CBD' },
  { value: 'CRYPTO', label: 'Crypto' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'FASHION', label: 'Fashion' },
  { value: 'FINANCE', label: 'Finance' },
  { value: 'FOOD', label: 'Food' },
  { value: 'HEALTH_AND_FITNESS', label: 'Health & Fitness' },
  { value: 'HOME_IMPROVEMENT', label: 'Home Improvement' },
  { value: 'LIFESTYLE', label: 'Lifestyle' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'NEWS', label: 'News' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'TECH', label: 'Tech' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'OTHER', label: 'Other' }
];

const PushedData: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<PushedDataRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<PushedDataRecord | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [categories, setCategories] = useState<Array<{ value: string; label: string }>>(FALLBACK_CATEGORIES);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPublisher, setSelectedPublisher] = useState('');
  
  // Export state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportPublishers, setExportPublishers] = useState<Array<{
    id: string;
    email: string;
    name: string;
    isMatched: boolean;
    recordCount: number;
  }>>([]);
  const [selectedExportPublisher, setSelectedExportPublisher] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  
  // Delete state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<'single' | 'bulk'>('single');
  const [recordToDelete, setRecordToDelete] = useState<PushedDataRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Fetch categories from API on component mount (shared cache)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const list = await getCachedCategories();
        if (list.length > 0) {
          setCategories(list);
        }
      } catch (error) {
        console.error('Error fetching categories from API, using fallback:', error);
        // Categories already initialized with FALLBACK_CATEGORIES
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    fetchData();
    if (user?.role === 'SUPER_ADMIN') {
      fetchExportPublishers();
    }
  }, []);

  // Fetch publishers for export dropdown
  const fetchExportPublishers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/data-final/publishers/pushed-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setExportPublishers(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch export publishers:', err);
    }
  };

  // Get unique publishers for filter dropdown
  const publishers = useMemo(() => {
    const uniquePublishers = new Set<string>();
    data.forEach(record => {
      if (record.publisherName) {
        uniquePublishers.add(record.publisherName);
      }
    });
    return Array.from(uniquePublishers).sort();
  }, [data]);

  // Filter data based on search and publisher filter
  const filteredData = useMemo(() => {
    const normalizeSearch = (value: string) =>
      value
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .split('?')[0]
        .split('#')[0]
        .split(':')[0];

    const searchNormalized = searchQuery ? normalizeSearch(searchQuery) : '';

    return data.filter(record => {
      // Search filter - match with or without http(s)/www
      const matchesSearch =
        searchNormalized === '' ||
        normalizeSearch(record.websiteUrl).includes(searchNormalized) ||
        record.websiteUrl.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Publisher filter
      const matchesPublisher = selectedPublisher === '' || 
        record.publisherName === selectedPublisher;
      
      return matchesSearch && matchesPublisher;
    });
  }, [data, searchQuery, selectedPublisher]);

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedPublisher('');
  };

  // Get category label from value
  const getCategoryLabel = (value: string | undefined): string => {
    if (!value) return 'Not set';
    const category = categories.find(cat => cat.value === value);
    return category ? category.label : value;
  };

  // Handle publisher export
  const handleExportByPublisher = async () => {
    if (!selectedExportPublisher) {
      setError('Please select a publisher to export');
      return;
    }

    setExporting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const selectedPublisher = exportPublishers.find(p => p.email === selectedExportPublisher);
      
      const response = await axios.get(
        `${API_BASE_URL}/data-final/export/pushed-publisher?publisherEmail=${encodeURIComponent(selectedExportPublisher)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const publisherName = selectedPublisher?.name || 'publisher';
      const sanitizedName = publisherName.replace(/[^a-zA-Z0-9]/g, '_');
      link.setAttribute('download', `pushed_${sanitizedName}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage(`Successfully exported ${selectedPublisher?.recordCount || 0} pushed records for ${publisherName}`);
      setShowExportDialog(false);
      setSelectedExportPublisher('');
    } catch (err: any) {
      console.error('Export error:', err);
      setError(err.response?.data?.message || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Paginated data
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/data-final/pushed`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Get pushed records directly from the new endpoint
      const pushedRecords = response.data.data || [];
      
      setData(pushedRecords);
      setError('');
    } catch (err: any) {
      console.error('Error fetching pushed data:', err);
      setError(err.response?.data?.message || 'Failed to fetch pushed data');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (record: PushedDataRecord) => {
    setSelectedRecord(record);
    setShowViewDialog(true);
  };

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(filteredData.map(record => record.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  // Delete handlers
  const handleDeleteClick = (record: PushedDataRecord) => {
    setRecordToDelete(record);
    setDeleteTarget('single');
    setShowDeleteDialog(true);
  };

  const handleBulkDeleteClick = () => {
    setDeleteTarget('bulk');
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const idsToDelete = deleteTarget === 'single' && recordToDelete 
        ? [recordToDelete.id] 
        : selectedIds;

      const response = await axios.delete(`${API_BASE_URL}/data-final/pushed`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { ids: idsToDelete }
      });

      if (response.data.success) {
        setSuccessMessage(`Successfully deleted ${idsToDelete.length} record(s)`);
        setSelectedIds([]);
        fetchData();
      }
    } catch (err: any) {
      console.error('Error deleting records:', err);
      setError(err.response?.data?.message || 'Failed to delete records');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setRecordToDelete(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      default:
        return 'default';
    }
  };

  const getNegotiationStatusColor = (status: string) => {
    switch (status) {
      case 'DONE':
        return 'success';
      case 'IN_PROGRESS':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Pushed Data
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View all sites that have been successfully transferred to the Link Management Tool
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Search and Filter Section */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, rowGap: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Search site URL"
              placeholder="Type to search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 260, minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')} edge="end" aria-label="clear search">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />

            {user?.role !== 'CONTRIBUTOR' && (
              <Autocomplete
                size="small"
                options={[
                  { id: '', label: 'All Publishers' },
                  ...publishers.map((publisher) => ({
                    id: publisher,
                    label: publisher
                  }))
                ]}
                value={
                  !selectedPublisher
                    ? { id: '', label: 'All Publishers' }
                    : { id: selectedPublisher, label: selectedPublisher }
                }
                onChange={(_, option) => setSelectedPublisher(option?.id || '')}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} label="Publisher" placeholder="Search publisher..." />
                )}
                sx={{ width: 260, minWidth: 260 }}
              />
            )}

            {(searchQuery || selectedPublisher) && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                startIcon={<ClearIcon fontSize="small" />}
                sx={{ height: 40 }}
              >
                Clear All
              </Button>
            )}

            {/* Export Button - Only for Super Admin */}
            {user?.role === 'SUPER_ADMIN' && exportPublishers.length > 0 && (
              <Button
                variant="outlined"
                color="info"
                size="small"
                onClick={() => setShowExportDialog(true)}
                startIcon={<span>📊</span>}
                sx={{ height: 40 }}
              >
                Export by Publisher
              </Button>
            )}
          </Box>

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">
                {filteredData.length === data.length 
                  ? `Total Pushed Sites: ${data.length}`
                  : `Showing ${filteredData.length} of ${data.length} sites`
                }
              </Typography>
              {selectedIds.length > 0 && (
                <Chip 
                  label={`${selectedIds.length} selected`} 
                  color="primary" 
                  size="small"
                  onDelete={() => setSelectedIds([])}
                />
              )}
            </Box>
            {selectedIds.length > 0 && (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDeleteClick}
              >
                Delete Selected ({selectedIds.length})
              </Button>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : data.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body1" color="text.secondary">
                No pushed sites yet. Sites will appear here after being transferred to the Link Management Tool.
              </Typography>
            </Box>
          ) : filteredData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body1" color="text.secondary">
                No sites match your search criteria.
              </Typography>
              <Button 
                variant="text" 
                onClick={handleClearFilters}
                sx={{ mt: 1 }}
              >
                Clear Filters
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredData.length}
                        checked={filteredData.length > 0 && selectedIds.length === filteredData.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell><strong>Site URL</strong></TableCell>
                    <TableCell align="center"><strong>Image Link</strong></TableCell>
                    <TableCell><strong>Publisher</strong></TableCell>
                    <TableCell><strong>DA</strong></TableCell>
                    <TableCell><strong>DR</strong></TableCell>
                    <TableCell><strong>Traffic</strong></TableCell>
                    <TableCell><strong>SS</strong></TableCell>
                    <TableCell><strong>Keywords</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                    <TableCell><strong>GB Base Price</strong></TableCell>
                    <TableCell><strong>LI Base Price</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Pushed At</strong></TableCell>
                    <TableCell><strong>Pushed By</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row) => (
                    <TableRow 
                      key={row.id} 
                      hover
                      selected={selectedIds.includes(row.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.includes(row.id)}
                          onChange={() => handleSelectOne(row.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {row.websiteUrl}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {row.imageLink ? (
                          <Tooltip title={row.imageLink}>
                            <IconButton
                              size="small"
                              component="a"
                              href={row.imageLink.startsWith('http') ? row.imageLink : `https://${row.imageLink}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.publisherName || <Typography variant="caption" color="text.secondary">Not set</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.da || <Typography variant="caption" color="text.secondary">-</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.dr || <Typography variant="caption" color="text.secondary">-</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.traffic || <Typography variant="caption" color="text.secondary">-</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.ss || <Typography variant="caption" color="text.secondary">-</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.keywords || <Typography variant="caption" color="text.secondary">-</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.category ? getCategoryLabel(row.category) : <Typography variant="caption" color="text.secondary">Not set</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.gbBasePrice ? `$${row.gbBasePrice}` : <Typography variant="caption" color="text.secondary">Not set</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.liBasePrice ? `$${row.liBasePrice}` : <Typography variant="caption" color="text.secondary">Not set</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={row.status} 
                          color={getStatusColor(row.status) as any}
                          size="small"
                          sx={{ color: row.status === 'ACTIVE' ? '#fff' : '#333', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(row.pushedAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.pushedByUser ? (
                          <Typography variant="body2">
                            {row.pushedByUser.firstName} {row.pushedByUser.lastName}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">Unknown</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              color="info"
                              onClick={() => handleView(row)}
                            >
                              <RemoveRedEyeIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteClick(row)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Pagination - show when data exists */}
          {filteredData.length > 0 && (
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100, 250, 500]}
              showFirstButton
              showLastButton
            />
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onClose={() => setShowViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Site Details</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">Site URL</Typography>
              <Typography variant="body1" sx={{ mb: 2, fontFamily: 'monospace', fontWeight: 'bold' }}>
                {selectedRecord.websiteUrl}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">Publisher Information</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {selectedRecord.publisherName || 'Not set'} ({selectedRecord.publisherEmail || 'No email'})
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">Metrics</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                DA: {selectedRecord.da || '-'} | DR: {selectedRecord.dr || '-'} | 
                Traffic: {selectedRecord.traffic || '-'} | SS: {selectedRecord.ss || '-'} | 
                Keywords: {selectedRecord.keywords || '-'}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">Pricing</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                GB Base Price: ${selectedRecord.gbBasePrice || 'Not set'} | 
                LI Base Price: ${selectedRecord.liBasePrice || 'Not set'}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">Transfer Information</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Main Project ID: {selectedRecord.mainProjectId}<br />
                Pushed At: {new Date(selectedRecord.pushedAt).toLocaleString()}<br />
                Pushed By: {selectedRecord.pushedByUser ? 
                  `${selectedRecord.pushedByUser.firstName} ${selectedRecord.pushedByUser.lastName}` : 
                  'Unknown'}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label={selectedRecord.status} color={getStatusColor(selectedRecord.status) as any} />
                <Chip label={selectedRecord.negotiationStatus} color={getNegotiationStatusColor(selectedRecord.negotiationStatus) as any} />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={showDeleteDialog} 
        onClose={() => !deleting && setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {deleteTarget === 'single' && recordToDelete ? (
              <>
                Are you sure you want to delete <strong>{recordToDelete.websiteUrl}</strong>?
                <br /><br />
                This will remove the record from the pushed data list. The site will remain in the main tool.
              </>
            ) : (
              <>
                Are you sure you want to delete <strong>{selectedIds.length}</strong> selected record(s)?
                <br /><br />
                This will remove the records from the pushed data list. The sites will remain in the main tool.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowDeleteDialog(false)} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export by Publisher Dialog */}
      <Dialog open={showExportDialog} onClose={() => setShowExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Pushed Data by Publisher</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Export all pushed records for a specific publisher as CSV file.
            </Alert>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Publisher</InputLabel>
              <Select
                value={selectedExportPublisher}
                label="Select Publisher"
                onChange={(e) => setSelectedExportPublisher(e.target.value)}
              >
                {exportPublishers.map((publisher) => (
                  <MenuItem key={publisher.email} value={publisher.email}>
                    <Box>
                      <Typography variant="body2">
                        {publisher.name} ({publisher.recordCount} records)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {publisher.email} • {publisher.isMatched ? 'Matched Publisher' : 'Contact'}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedExportPublisher && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <strong>Export will include:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>All pushed records for this publisher</li>
                  <li>Complete site details and metrics</li>
                  <li>Transfer information (pushed date, main project ID)</li>
                </ul>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowExportDialog(false);
            setSelectedExportPublisher('');
          }} disabled={exporting}>
            Cancel
          </Button>
          <Button 
            onClick={handleExportByPublisher} 
            variant="contained" 
            color="info"
            disabled={exporting || !selectedExportPublisher}
            startIcon={exporting ? <CircularProgress size={16} /> : <span>📊</span>}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PushedData;
